'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchBanners } from '@eliadmin/core/src/api/general/general-api';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { ViewportBannerImage } from '@eliadmin/core/src/components/image/viewport-banner-image';
import { BANNER_IMAGE_CONFIG } from '@eliadmin/core/src/constants/banner-image-config';
import { imageDimensions } from '@eliadmin/core/src/utils/image-dimensions';

export type BannerGridProps = {
  className?: {
    main?: string;
    bannerLink?: string;
    bannerImage?: string;
  };
};

export function BannerGrid(props: BannerGridProps) {
  const locale = useLocaleStore(s => s.currentLocale);

  const { data: banners } = useSuspenseQuery({
    queryKey: [CacheKey.BANNERS, locale, { type: 'banner' }],
    queryFn: () => fetchBanners({ locale, query: { where: { type: { equals: 'banner' } } } }),
    staleTime: 60_000,
  });

  const desktopBanners = banners.data
    ?.filter(i => i.types?.includes('desktop') && i.image)
    .slice(0, 1);
  const mobileBanners: any[] = [];

  if (!desktopBanners.length && !mobileBanners.length) return null;

  return (
    <>
      <div
        className={cn(
          'hidden flex-wrap gap-[16px] md:flex',
          'ea-banner-grid',
          props.className?.main,
        )}
      >
        {desktopBanners.map(banner => {
          if (!banner.image) return null;
          const dimensions =
            imageDimensions(banner.image.metadata) ??
            imageDimensions(BANNER_IMAGE_CONFIG.dimensions[banner.image.url]);
          return (
            <div
              className={cn(
                'h-full w-full min-w-full flex-1 md:min-w-[calc(50%-16px)]',
                'ea-banner-grid__container',
              )}
              key={`${banner.url}-${banner.image.url}-${banner.image.name}`}
            >
              <Link
                href={banner.url ?? '#'}
                className={cn('ea-banner-grid__link', props.className?.bannerLink)}
                prefetch={false}
              >
                <ViewportBannerImage
                  viewport="desktop"
                  alt={resolveImageAlt(banner?.image, banner?.name, 'image')}
                  src={banner.image.url}
                  width={dimensions?.width ?? 0}
                  height={dimensions?.height ?? 0}
                  sizes="(min-width: 1280px) 1248px, 100vw"
                  quality={90}
                  className={cn(
                    'h-auto w-full bg-primary/10 object-contain',
                    'ea-homepage-banner-grid__link-image',
                    props.className?.bannerImage,
                  )}
                />
              </Link>
            </div>
          );
        })}
      </div>
      <div
        className={cn(
          'flex flex-wrap gap-[16px] md:hidden',
          'ea-banner-grid',
          props.className?.main,
        )}
      >
        {mobileBanners.map(banner => {
          if (!banner.image) return null;
          return (
            <div
              className={cn(
                'h-full w-full min-w-full flex-1 md:min-w-[calc(50%-16px)]',
                'ea-banner-grid__container',
              )}
              key={`${banner.url}-${banner.image.url}-${banner.image.name}`}
            >
              <Link
                href={banner.url ?? '#'}
                className={cn('ea-banner-grid__link', props.className?.bannerLink)}
                prefetch={false}
              >
                <Image
                  alt={resolveImageAlt(banner?.image, banner?.name, 'image')}
                  src={banner.image.url}
                  width="0"
                  height="0"
                  sizes="100vw"
                  quality={90}
                  className={cn(
                    'w-full object-contain',
                    'ea-homepage-banner-grid__link-image',
                    props.className?.bannerImage,
                  )}
                />
              </Link>
            </div>
          );
        })}
      </div>
    </>
  );
}
